import React, { useCallback, useEffect, useMemo, useState } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { getTransaction, getLedgerDetails } from "../../services/services";
import moment from "moment";
import { toastError } from "../../utils";
import debounce from "lodash/debounce";
import Pagination from "../../components/Pagination";
import Loading from "../../components/loading";
import SearchFilter from "./searchFilter";
import DataTable from "react-data-table-component";
import PartyCardDetail from "../party/partyCardDetail";

const TransactionPage = () => {
  const [searchBy, setSearchBy] = useState("name");
  const [searchName, setSearchName] = useState("");
  const [searchReferenceType, setSearchReferenceType] = useState("");
  const [searchFromDate, setSearchFromDate] = useState("");
  const [searchToDate, setSearchToDate] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);

  const [loading, setLoading] = useState(true);
  const [parties, setParties] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const debouncedFetchData = useCallback(
    debounce(async (page = 1, pageLimit = limit) => {
      setLoading(true);
      try {
        const payload = {
          name: searchName.trim(),
          referenceType: searchReferenceType,
          from: searchFromDate,
          to: searchToDate,
          page,
          limit: pageLimit,
        };

        Object.keys(payload).forEach(
          (key) => payload[key] === "" && delete payload[key],
        );

        const response = await getTransaction(payload);
        const { ledgerEntries = [], pagination = {} } = response.data;

        setTransactions(ledgerEntries);
        setCurrentPage(pagination.page || 1);
        setTotalPages(pagination.totalPages || 1);
      } catch (error) {
        console.error("Error fetching transactions:", error);
        toastError(
          error.response?.data?.error || "Failed to fetch transactions",
        );
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    }, 400),
    [searchName, searchReferenceType, searchFromDate, searchToDate, limit],
  );

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        // minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        textTransform: "uppercase",
      },
    },
  };

  useEffect(() => {
    debouncedFetchData(1, limit);
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  const handleSelectTransaction = (transaction) => {
    setSelectedTransaction(transaction);
  };

  const handleSearch = (params = {}) => {
    setSearchBy(
      params.name
        ? "name"
        : params.referenceType
          ? "referenceType"
          : params.from
            ? "date"
            : "",
    );

    setSearchName(params.name || "");
    setSearchReferenceType(params.referenceType || "");
    setSearchFromDate(params.from || "");
    setSearchToDate(params.to || "");

    setCurrentPage(1);
    debouncedFetchData(1, limit);
  };

  const handlePageChange = (page) => {
    if (!loading && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      debouncedFetchData(page, limit);
    }
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
    debouncedFetchData(1, newLimit);
  };

  /* DATA TABLE COLUMNS */
  const columns = useMemo(
    () => [
      {
        name: "S. No.",
        width: "6rem",
        cell: (row, index) => (currentPage - 1) * limit + index + 1,
      },
      {
        name: "Party Name",
        selector: (row) =>
          row.party?.name ||
          row.expenseTitle ||
          row.paidTo ||
          row.partyName ||
          "-",
        // width: "15rem",
      },
      {
        name: "Reference Type",
        // width: "12rem",
        cell: (row) => (
          <strong
            className={
              row.referenceType === "purchase" ||
              row.referenceType === "purchaseOrder"
                ? "text-danger text-nowrap"
                : row.referenceType === "sale" ||
                    row.referenceType === "saleOrder" ||
                    row.referenceType === "payment-in" ||
                    row.referenceType === "payment-out"
                  ? "text-success text-nowrap"
                  : "text-muted text-nowrap"
            }
          >
            {row.referenceType.toUpperCase()}
          </strong>
        ),
      },
      {
        name: "Total (₹)",
        // width: "12rem",
        // right: true,
        cell: (row) => Math.abs(row.totalAmount || row.amount || 0).toFixed(2),
      },
      // {
      //   name: "(₹) Due",
      //   width: "15rem",
      //   right: true,
      //   cell: (row) => Math.abs(row.dueAmount || 0).toFixed(2),
      // },
      {
        name: "Paid / Received (₹)",
        // width: "12rem",
        // right: true,
        cell: (row) =>
          row.referenceType === "opening" ? (
            "-"
          ) : (
            <span className="text-nowrap">
              {row.referenceType === "purchase" ||
              row.referenceType === "payment-out"
                ? Math.abs(row.paidAmount || 0).toFixed(2)
                : Math.abs(row.receivedAmount || 0).toFixed(2)}
            </span>
          ),
      },
      // {
      //   name: "(₹) Balance",
      //   width: "12rem",
      //   right: true,
      //   cell: (row) => (
      //     <span
      //       className={
      //         row?.balance === 0
      //           ? "text-muted"
      //           : row?.balance < 0
      //           ? "text-danger"
      //           : "text-success"
      //       }
      //     >
      //       {Math.abs(row?.balance || 0).toFixed(2)}
      //     </span>
      //   ),
      // },
      {
        name: "Date",
        // width: "15rem",
        // right: true,
        cell: (row) => moment(row.date).format("DD MMM, YY"),
      },
      // {
      //   name: "Action",
      //   width: "10%",
      //   center: true,
      //   cell: () => (
      //     <i
      //       className="bi bi-eye btn btn-light shadow-sm btn-sm rounded px-2"
      //       style={{ fontSize: 14 }}
      //     />
      //   ),
      // },
    ],
    [currentPage, limit],
  );

  return (
    <>
      {/* {loading ? <Loading /> : */}
      <div id="main">
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap">
          <h4 className="mb-0 custom-title">Manage Transactions</h4>
          <div className="d-flex align-items-center gap-2">
            <SearchFilter parties={parties} onSearch={handleSearch} />
          </div>
        </div>
        <div className="row">
          <div className="col-12 transaction-list">
            <div className="card shadow-sm rounded mb-3 bg-white">
              <div className="card-body p-0 rounded">
                <DataTable
                  columns={columns}
                  data={transactions}
                  customStyles={customStyles}
                  highlightOnHover
                  striped
                  onRowClicked={handleSelectTransaction}
                  progressComponent={<Loading />}
                  progressPending={loading}
                  noDataComponent={
                    <>
                      {loading ? (
                        <Loading />
                      ) : (
                        <div className="py-4 text-muted text-center">
                          <i className="bi bi-inbox fs-1 mb-2 d-block"></i>
                          No transaction records found
                        </div>
                      )}
                    </>
                  }
                />
              </div>
            </div>
            {transactions.length > 0 && (
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                limit={limit}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>

      {/* {selectedTransaction && (
        <TransactionDetail
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )} */}
      {selectedTransaction && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
            style={{ zIndex: 1049 }}
            onClick={() => setSelectedTransaction(null)}
          />
          <PartyCardDetail
            ledgerEntry={selectedTransaction}
            onClose={() => setSelectedTransaction(null)}
            showActions={false}
            invoiceNumber={selectedTransaction?.invoiceNumber}
          />
        </>
      )}
    </>
  );
};

export default headerLayoutWrapper(TransactionPage);
